import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { fn } from '@ember/helper';
import { and, eq, or } from 'ember-truth-helpers';
import { t } from 'ember-intl';
import can from 'ember-can/helpers/can';
import {
  HdsTable,
  HdsTooltipButton,
  HdsIcon,
  HdsDropdown,
} from '@hashicorp/design-system-components/components';
import Flex from 'core/components/flex';
import EditMemberRole from 'hcp/components/iam/groups/modals/edit-member-role';
import RemoveMember from 'hcp/components/iam/groups/modals/remove-member';

/**
 *
 * `IamGroupMembersListTable` shows a group's members.
 *
 *
 * ```
 * <Iam::Groups::Members::ListTable
 *  @group={{foo}}
 *  @rawGroupPolicy={{foo}}
 *  @groupLevelPermissions={{foo}}
 *  @scimEnabled={{true}}
 *  @isProjectContext={{false}}
 *  @members={{foo}}
 * />
 * ```
 *
 * @class IamGroupMembersListTable
 *
 */
export default class IamGroupMembersListTable extends Component {
  /**
   * @argument group
   * @type {Object}
   * @required
   */
  /**
   * @argument rawGroupPolicy
   * @type {Object}
   * @required
   */
  /**
   * @argument groupLevelPermissions
   * @type {Array}
   * @required
   */
  /**
   * @argument scimEnabled
   * @type {Boolean}
   * @required
   */
  /**
   * @argument isProjectContext
   * @type {Boolean}
   * @optional
   */
  /**
   * @argument members
   * @type {Array}
   * @required
   */

  @service intl;
  @service abilities;

  @tracked memberToRemove;
  @tracked isRemoveMemberModalActive = false;

  @tracked memberToEditRole;
  @tracked isRoleModalActive = false;

  // eslint-disable-next-line ember/no-tracked-properties-from-args
  @tracked groupPolicy = this.args.rawGroupPolicy;

  get projectColumns() {
    return [
      {
        key: 'name',
        label: this.intl.t(
          'components.iam.groups.members.list-table.table.headings.name'
        ),
      },
      {
        key: 'email',
        label: this.intl.t(
          'components.iam.groups.members.list-table.table.headings.email'
        ),
      },
      {
        key: 'principalType',
        label: this.intl.t(
          'components.iam.groups.members.list-table.table.headings.type'
        ),
      },
      {
        key: 'groupMemberRole',
        label: this.intl.t(
          'components.iam.groups.members.list-table.table.headings.role'
        ),
      },
    ];
  }

  get columns() {
    const headers = [];
    headers.push({
      key: 'email',
      label: this.intl.t(
        'components.iam.groups.members.list-table.table.headings.email'
      ),
    });
    headers.push({
      key: 'principalType',
      label: this.intl.t(
        'components.iam.groups.members.list-table.table.headings.type'
      ),
    });
    headers.push({
      key: 'groupMemberRole',
      label: this.intl.t(
        'components.iam.groups.members.list-table.table.headings.role'
      ),
    });
    if (this.abilities.can('delete group')) {
      headers.push({
        key: '',
        label: '',
      });
    }
    return headers;
  }

  openRemoveModal = (member) => {
    this.isRemoveMemberModalActive = true;
    this.memberToRemove = member;
  };

  closeRemoveModal = () => {
    this.isRemoveMemberModalActive = false;
    this.memberToRemove = null;
  };

  openRoleModal = (member) => {
    this.isRoleModalActive = true;
    this.memberToEditRole = member;
  };

  closeRoleModal = () => {
    this.isRoleModalActive = false;
    this.memberToEditRole = null;
  };

  onPolicyUpdateSuccess = (rawGroupPolicy) => {
    this.groupPolicy = { ...rawGroupPolicy };
  };

  <template>
    {{#let
      (and (eq @scimEnabled true) (eq @group.scimSynchronized true))
      as |scimManaged|
    }}
      <HdsTable
        @caption={{t "components.iam.groups.members.list-table.table.caption"}}
        @valign="middle"
        data-test-groups-members-list-table
      >
        <:head as |H|>
          <H.Tr>
            <H.Th>{{t
                "components.iam.groups.members.list-table.table.headings.name"
              }}</H.Th>
            <H.Th>{{t
                "components.iam.groups.members.list-table.table.headings.email"
              }}</H.Th>
            <H.Th>
              <Flex @alignItems="center" @direction="row" @gap="2xs">
                {{t
                  "components.iam.groups.members.list-table.table.headings.role"
                }}
                <HdsTooltipButton
                  @isInline={{true}}
                  @text="{{t
                    'components.iam.groups.members.list-table.table.headings.tooltip.text'
                  }}"
                  aria-label="{{t
                    'components.iam.groups.members.list-table.table.headings.tooltip.aria-label'
                  }}"
                  data-test-groups-members-list-table-role-tooltip
                >
                  <HdsIcon @name="info" @isInline={{true}} />
                </HdsTooltipButton>
              </Flex>
            </H.Th>
            {{#if
              (and
                (can
                  "update-membership group"
                  groupLevelPermissions=@groupLevelPermissions
                )
                (eq @isProjectContext false)
                (eq scimManaged false)
              )
            }}
              <H.Th>
                {{t
                  "components.iam.groups.members.list-table.table.headings.actions"
                }}
              </H.Th>
            {{/if}}
          </H.Tr>
        </:head>
        <:body as |B|>
          {{#each @members as |member|}}
            <B.Tr data-test-group-member-item>
              <B.Td data-test-group-member-name>
                {{#if (or member.name member.email)}}
                  {{or member.name member.email}}
                {{else}}
                  —
                {{/if}}
              </B.Td>
              <B.Td data-test-group-member-email>
                {{#if member.email}}
                  {{member.email}}
                {{else}}
                  -
                {{/if}}
              </B.Td>
              <B.Td data-test-group-member-role>
                {{#if (eq member.isManager true)}}
                  {{t "components.iam.groups.members.list-table.roles.manager"}}
                {{else}}
                  {{t "components.iam.groups.members.list-table.roles.member"}}
                {{/if}}
              </B.Td>
              {{#if
                (and
                  (can
                    "update-membership group"
                    groupLevelPermissions=@groupLevelPermissions
                  )
                  (eq @isProjectContext false)
                  (eq scimManaged false)
                )
              }}
                <B.Td data-test-group-member-remove-dropdown>
                  <Flex @justifyContent="end">
                    <HdsDropdown data-test-more-menu as |dd|>
                      <dd.ToggleIcon
                        @icon="more-horizontal"
                        @text={{t "components.form.more"}}
                        @hasChevron={{false}}
                        data-test-more-menu-trigger
                        data-test-group-item-menu
                      />
                      <dd.Interactive
                        @icon="edit"
                        @text={{t
                          "components.iam.groups.form.edit-member-role.cta"
                        }}
                        id="modal-dialog-edit-role-open-button-{{member.id}}"
                        {{on "click" (fn this.openRoleModal member)}}
                        data-test-groups-edit-member-role-button
                      />
                      <dd.Interactive
                        @color="critical"
                        @icon="trash"
                        @text={{t
                          "components.iam.groups.form.remove-member.cta"
                        }}
                        id="modal-dialog-delete-open-button-{{member.id}}"
                        {{on "click" (fn this.openRemoveModal member)}}
                        data-test-groups-remove-member-button
                      />
                    </HdsDropdown>
                  </Flex>
                </B.Td>
              {{/if}}
            </B.Tr>
          {{/each}}
        </:body>
      </HdsTable>
    {{/let}}
    <EditMemberRole
      @isActive={{this.isRoleModalActive}}
      @closeModal={{this.closeRoleModal}}
      @group={{@group}}
      @memberToEditRole={{this.memberToEditRole}}
      @groupPolicy={{this.groupPolicy}}
      @onPolicyUpdateSuccess={{this.onPolicyUpdateSuccess}}
    />
    <RemoveMember
      @isActive={{this.isRemoveMemberModalActive}}
      @closeModal={{this.closeRemoveModal}}
      @group={{@group}}
      @memberToRemove={{this.memberToRemove}}
    />
  </template>
}
